import React, { useState } from 'react';
import DkFlag from '../../../../assets/icons/svg/DkFlag';
import { Header } from '../../Header/Header';
import { Icon } from '../../Icon/Icon';
import { Link } from '../../Link/Link';
import { Logo } from '../../Logo/Logo';
import { Paragraph } from '../../Paragraph/Paragraph';
import { CountDown } from '../../CountDown/CountDown';
import { GameGraphics } from '../../GameGraphics/GameGraphics';
import { GameActions } from '../../TileActions/GameActions';
import { TilePill } from '../../TilePill/TilePill';
import type { TileProps } from '../tiles';
import { GAME_UNAVAILABLE } from '../../../helpers/tileHelper';
import { track } from '../../../helpers/tracking';
import { isMobile, isTablet } from '@SharedScripts/Utils/DeviceClass';

export const SmallTile = ({
  title,
  type,
  gameGraphics,
  minWager,
  favourited,
  navigationUrl,
  navigationUrlWithLaunchParam,
  potSize,
  playersText,
  dealerName,
  buttonEnabled = false,
  openFrom,
  isTableClosed,
  isUnavailable,
  primaryButtonCallback,
  className,
  onClick,
  tabIndex,
  addToSearchInteractions
}: TileProps) => {
  const [graphicsLoading, setGraphicsLoading] = useState(true);
  const [graphicsError, setGraphicsError] = useState(false);

  const linkAriaLabel = `Start spil ${title}`;

  const graphicsDisabled =
    isUnavailable || (type !== 'Casino' && isTableClosed);

  const pillText = type === 'Casino' ? potSize : playersText;
  const pillIcon = type === 'Casino' ? 'jackpot' : 'players';

  const shouldRenderLogo = (graphicsError && !graphicsDisabled) || !gameGraphics?.backgroundUrl;

  return (
    <article
      data-tracking-meta-data={JSON.stringify({
        tileType: 'Small',
        action: isMobile() || isTablet() ? 'click game tile' : 'game open',
        label: isMobile() || isTablet() ? 'mobile' : 'desktop',
      })}
      onClick={onClick}
      className={`group w-full relative grid 
      ${!buttonEnabled && isUnavailable ?
      'grid-cols-[8rem_1fr_9rem] md:grid-cols-[9.6rem_1fr_10rem] is-tablet-detected:md:grid-cols-[9.6rem_1fr_9rem] is-tablet-detected:lg:grid-cols-[9.6rem_1fr_10rem]'
      :
      (!buttonEnabled && isTableClosed ? 'grid-cols-[8rem_1fr_12rem] md:grid-cols-[9.6rem_1fr_12rem]'
        :
        'grid-cols-[8rem_1fr_auto] md:grid-cols-[9.6rem_1fr_auto]'
      )}
      rounded-32 transition-200 is-desktop-detected:hover:bg-neutrals-white/50 after:items-center is-desktop-detected:p-12 ${className}`}
    >

      <div onClick={(event) => {
        track(event);
        addToSearchInteractions?.();
      }} className='absolute top-0 right-0 bottom-0 left-0 z-20'
      >
        <Link
          className='block w-full h-full rounded-32 focus-visible:ring-primary'
          href={navigationUrl}
          ariaLabel={linkAriaLabel}
          disabled={isUnavailable || !navigationUrl}
          tabIndex={tabIndex}
        />
      </div>

      <div className='relative aspect-square flex w-80 md:w-96'>
        <GameGraphics
          {...gameGraphics}
          imageWidth={100}
          isLoading={graphicsLoading}
          onLoaded={() => setGraphicsLoading(false)}
          onError={() => setGraphicsError(true)}
          error={graphicsError}
          disabled={graphicsDisabled}
          lazyLoad={true}
          disableCache={type === 'Live'}
          useDarkGradient={type === 'Live' && !isUnavailable}
          useLogoAsForeground={type !== 'Live'}
          useThumbnailAsBackground={type !== 'Live'}
          className='overflow-hidden'
          rounded='rounded-24'
          backgroundStyle={type === 'Live' ? 'object-top' : ''}
        />

        {shouldRenderLogo && (
          <Logo
            className='z-10 m-auto w-24'
          />
        )}

        {!isUnavailable && isTableClosed && (
          <CountDown
            className='relative my-auto text-white w-full text-center text-14 px-8'
            openingTime={openFrom ?? ''}
            openingTimeLabel='Åbner om'
          />
        )}
      </div>

      <div className='flex flex-col justify-center text-purple-primary fill-purple-primary ml-4 mr-8 flex-1'>

        {title && <Header
          as='h3'
          size='xs'
          className={'font-bold pl-8'}
          truncate={2}
        >
          {title}
        </Header>}

        <>
          <div className='flex items-center'>
            {pillText && <TilePill
              text={pillText}
              icon={pillIcon}
              background='transparent'
              className='!py-0 md:!py-4'
            />}

            {dealerName && (
              <Icon
                className='right-8'
                icon={<DkFlag/>}
                size='small'
                ariaLabel='Danish dealer'
              />
            )}
          </div>

          {minWager && (
            <TilePill
              text={minWager}
              icon='bid'
              background='transparent'
              className='!py-0'
            />
          )}
        </>
      </div>

      {!isUnavailable && (
        <GameActions
          href={navigationUrlWithLaunchParam}
          linkAriaLabel={linkAriaLabel}
          favourited={favourited}
          favoriteSize='medium'
          iconVariant={'favorite-logo-no-bg'}
          buttonEnabled={buttonEnabled}
          favouritedAriaLabel={`Føj ${title} til favouritter`}
          primaryButtonCallback={primaryButtonCallback}
          addToSearchInteractions={addToSearchInteractions}
          hideFavouriteBackground={true}
          className='z-30 items-center bottom-0 self-center
                is-desktop-detected:opacity-0 transition-opacity duration-100 ease-in-out
                is-desktop-detected:group-hover:opacity-100 is-desktop-detected:group-focus-within:opacity-100 is-desktop-detected:group-focus:opacity-100'
          tabIndex={tabIndex}
          isTableClosed={isTableClosed}
        />
      )}

      {isUnavailable && (
        <Paragraph
          size={12}
          weight='medium'
          truncate={4}
          className='relative self-center text-center my-auto max-w-[13ch] text-purple-primary'
        >
          {GAME_UNAVAILABLE}
        </Paragraph>
      )}
    </article>
  );
};
